import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import useSEO from "@/hooks/useSEO";
import { BreadcrumbSchema, ServiceSchema } from "@/components/SEOStructuredData";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import SkipToContent from "@/components/SkipToContent";
import ScrollReveal from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Mic, Video, Clapperboard, Camera, Film, Languages, Briefcase, LucideIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useMemo } from "react";
import { getIconByName } from "@/components/admin/IconSelector";
import { sanitizeHtml } from "@/lib/sanitize";

interface PageContent {
  section_key: string;
  content_en: string;
  content_fr: string;
  is_hidden: boolean;
  display_order: number;
}

const Services = () => {
  const { t, i18n } = useTranslation();
  const { localizedPath } = useLocalizedPath();
  const currentLang = i18n.language?.substring(0, 2) || "en";

  useSEO({
    title: t("servicesPage.title"),
    description: t("seo.servicesDescription", "Voiceover casting, on-camera talent, production support, photography, video production, and language services from First Stage Talent."),
  });

  // Fetch page content from database
  const { data: pageContent } = useQuery({
    queryKey: ["services-page-content"],
    queryFn: async () => {
      const { data } = await supabase
        .from("page_content")
        .select("section_key, content_en, content_fr, is_hidden, display_order")
        .eq("page_slug", "services");
      return data as PageContent[] | null;
    },
  });

  // Helper to get content by section key
  const getContent = (key: string, fallback: string = "") => {
    const item = pageContent?.find(p => p.section_key === key);
    if (!item) return fallback;
    return currentLang === "fr" ? item.content_fr : item.content_en;
  };

  // Helper to get use cases as array (split by newlines)
  const getUseCases = (key: string, fallbackKey?: string): string[] => {
    const content = getContent(key, "");
    if (content) {
      return content.split("\n").filter(line => line.trim() !== "");
    }
    // Fallback to translation if key provided
    if (fallbackKey) {
      const translated = t(fallbackKey, { returnObjects: true });
      return Array.isArray(translated) ? (translated as string[]) : [];
    }
    return [];
  };

  // Helper to check if a section is hidden
  const isSectionHidden = (sectionKey: string) => {
    const item = pageContent?.find(p => p.section_key === sectionKey);
    return item?.is_hidden ?? false;
  };

  // Helper to strip HTML tags for plain text contexts
  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  // Dynamically build services from page content
  const services = useMemo(() => {
    if (!pageContent) return [];
    
    // Find all unique prefixes that have _title, _description, _usecases with their display_order
    const prefixMap = new Map<string, number>();
    pageContent.forEach(item => {
      const match = item.section_key.match(/^(.+)_(title|description|usecases|icon)$/);
      if (match && match[1] !== "services" && match[1] !== "cta") {
        if (!prefixMap.has(match[1])) {
          prefixMap.set(match[1], item.display_order || 0);
        }
      }
    });

    // Filter to only include prefixes that have all three sections and title is not hidden
    return Array.from(prefixMap.entries())
      .filter(([prefix]) => {
        const titleItem = pageContent.find(p => p.section_key === `${prefix}_title`);
        const descItem = pageContent.find(p => p.section_key === `${prefix}_description`);
        const usecasesItem = pageContent.find(p => p.section_key === `${prefix}_usecases`);
        return titleItem && descItem && usecasesItem && !titleItem.is_hidden;
      })
      .sort((a, b) => a[1] - b[1])
      .map(([prefix]) => {
        // Get icon from database or fall back to Briefcase
        const iconItem = pageContent.find(p => p.section_key === `${prefix}_icon`);
        const iconName = iconItem?.content_en || "Briefcase";
        
        return {
          prefix,
          icon: getIconByName(iconName),
          sectionKey: `${prefix}_title`,
          title: getContent(`${prefix}_title`, ""),
          description: getContent(`${prefix}_description`, ""),
          useCases: getUseCases(`${prefix}_usecases`),
        };
      });
  }, [pageContent, currentLang]);

  const breadcrumbs = [
    { name: currentLang === "fr" ? "Accueil" : "Home", url: `https://firststagetalent.com/${currentLang}` },
    { name: stripHtml(getContent("services_title", t("servicesPage.title"))), url: `https://firststagetalent.com/${currentLang}/services` },
  ];

  return (
    <div className="min-h-screen">
      <BreadcrumbSchema items={breadcrumbs} />
      <ServiceSchema 
        name="Voiceover & Acting Talent Services" 
        description="Professional voiceover casting, on-camera talent, production support, photography, video production, and language services across Canada."
      />
      <SkipToContent />
      <Header />
      <main id="main-content" tabIndex={-1} className="outline-none">
        <PageHero 
          title={stripHtml(getContent("services_title", t("servicesPage.title")))} 
          subtitle={stripHtml(getContent("services_subtitle", t("servicesPage.subtitle")))}
        />
        
        {/* Services List */}
        <section className="section-padding">
          <div className="container-wide">
            <div className="space-y-24">
              {services.map((service, index) => (
                <div 
                  key={service.prefix}
                  className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-start ${
                    index % 2 === 1 ? "lg:flex-row-reverse" : ""
                  }`}
                >
                  <ScrollReveal 
                    animation={index % 2 === 0 ? "slideLeft" : "slideRight"}
                    className={index % 2 === 1 ? "lg:order-2" : ""}
                  >
                    <div className="w-16 h-16 bg-primary text-primary-foreground flex items-center justify-center mb-6">
                      <service.icon size={28} />
                    </div>
                    <h2 className="heading-section mb-4 text-primary">{stripHtml(service.title)}</h2>
                    <div 
                      className="text-editorial text-lg mb-8 prose prose-p:my-2"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(service.description) }}
                    />
                    <Button asChild variant="sectionCta">
                      <Link to={`${localizedPath("/contact")}?service=${service.prefix}`}>
                        {t("servicesPage.getStarted")}
                      </Link>
                    </Button>
                  </ScrollReveal>
                  
                  <ScrollReveal 
                    animation={index % 2 === 0 ? "slideRight" : "slideLeft"}
                    className={`bg-secondary p-8 md:p-10 ${index % 2 === 1 ? "lg:order-1" : ""}`}
                  >
                    <ul className="space-y-0">
                      {service.useCases.map((useCase) => (
                        <li key={useCase} className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 bg-primary mt-2 flex-shrink-0" />
                          <Link 
                            to={`${localizedPath("/contact")}?service=${service.prefix}&genre=${encodeURIComponent(useCase)}`}
                            className="text-muted-foreground hover:text-primary transition-colors"
                          >
                            {useCase}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </ScrollReveal>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        {!isSectionHidden("cta_title") && (
          <section className="section-padding bg-primary text-primary-foreground">
            <div className="container-wide">
              <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
                {/* Left: Title and CTA */}
                <div className="text-center md:text-left">
                  <ScrollReveal>
                    <h2 className="heading-section mb-4">
                      {stripHtml(getContent("cta_title", t("servicesPage.notSure")))}
                    </h2>
                  </ScrollReveal>
                  <ScrollReveal delay={0.1}>
                    <p className="text-primary-foreground/80 mb-6 text-lg">
                      {getContent("cta_intro", t("servicesPage.notSureDesc"))}
                    </p>
                  </ScrollReveal>
                  <ScrollReveal delay={0.2}>
                    <Button
                      asChild
                      variant="sectionCta"
                      className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                    >
                      <Link to={localizedPath("/contact")}>{t("servicesPage.letsTalk")}</Link>
                    </Button>
                  </ScrollReveal>
                </div>
                
                {/* Right: Hours and Contact Info */}
                <ScrollReveal delay={0.15}>
                  <div className="bg-primary-foreground/10 rounded-lg p-6 md:p-8">
                    {/* Hours */}
                    <h3 className="font-display font-medium text-primary-foreground mb-4">
                      {getContent("hours_title", "Hours of Operation")}
                    </h3>
                    <ul className="space-y-1 text-sm text-primary-foreground/80 mb-6">
                      {[
                        { openKey: "hours_monday_open", closeKey: "hours_monday_close", day: { en: "Monday", fr: "Lundi" } },
                        { openKey: "hours_tuesday_open", closeKey: "hours_tuesday_close", day: { en: "Tuesday", fr: "Mardi" } },
                        { openKey: "hours_wednesday_open", closeKey: "hours_wednesday_close", day: { en: "Wednesday", fr: "Mercredi" } },
                        { openKey: "hours_thursday_open", closeKey: "hours_thursday_close", day: { en: "Thursday", fr: "Jeudi" } },
                        { openKey: "hours_friday_open", closeKey: "hours_friday_close", day: { en: "Friday", fr: "Vendredi" } },
                      ].map(({ openKey, closeKey, day }) => {
                        const openTime = getContent(openKey, "");
                        const closeTime = getContent(closeKey, "");
                        const isClosed = openTime.toLowerCase() === "closed" || openTime.toLowerCase() === "fermé";
                        return (
                          <li key={openKey} className={isClosed ? "text-primary-foreground/50" : ""}>
                            <span className="inline-block w-24">{currentLang === "fr" ? day.fr : day.en}:</span>
                            {isClosed ? (
                              <span className="italic">{currentLang === "fr" ? "Fermé" : "Closed"}</span>
                            ) : (
                              <span>{openTime} - {closeTime}</span>
                            )}
                          </li>
                        );
                      })}
                      <li className="pt-1 text-primary-foreground/60 italic">
                        {getContent("hours_weekend", "Closed weekends and all Statutory holidays.")}
                      </li>
                    </ul>
                    
                    {/* Contact */}
                    <div className="border-t border-primary-foreground/20 pt-4 space-y-2 text-sm">
                      <p className="text-primary-foreground/80">
                        <span className="text-primary-foreground font-medium">Email: </span>
                        <a 
                          href={`mailto:${getContent("contact_email", "agent@firststage.ca")}`}
                          className="underline hover:text-primary-foreground"
                        >
                          {getContent("contact_email", "agent@firststage.ca")}
                        </a>
                      </p>
                      <p className="text-primary-foreground/80">
                        <span className="text-primary-foreground font-medium">Cell: </span>
                        {getContent("contact_phone", "1.613.447.7435")}
                      </p>
                      <p className="text-primary-foreground/60 text-xs italic pt-1">
                        {getContent("contact_note", "If outside of Canada or the U.S., please call on WhatsApp.")}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Services;
